import {
    convertDateResponse,
    Feedback,
    FeedbackDetails,
    NoFeedback,
} from "@relode/core";
import { convertFileResponse } from "@relode/core/backend/responses";
import {
    CandidateInterview,
    JobCandidateStatus,
    JobSalaryType,
    JobStatus,
    JobType,
    Tag,
} from "@relode/core/domain";

import { converters } from ".";
import {
    CandidateActions,
    CandidateAdditionalInfo,
    CandidateAvailability,
    CandidateDetails,
    CandidateHighlights,
    CandidateHiredDetails,
    CandidateJobDetails,
} from "../../types";
import {
    CandidateAdditionalInfoResponse,
    CandidateAvailabilityResponse,
    CandidateInterviewDetailsResponse,
    CandidateJobDetailsResponse,
    ClientUserResponse,
    FeedbackDetailsResponse,
    FeedbackResponse,
    HiredDetailsResponse,
    LeadActionsResponse,
    LeadHighlightsResponse,
    LeadJobDetailResponse,
} from "../controllers/candidates";
import { TagResponse } from "../controllers/tags/tagResponse";
import { convertLeadJobQualificationsResponse } from "./convertLeadJobQualificationsResponse";

// TODO convert all uses of classes (`new`) to interfaces

const convertCandidateHighlights = (
    response: LeadHighlightsResponse
): CandidateHighlights => {
    if (!response) return null;

    const highlights: CandidateHighlights = {
        leadId: response.leadId,
        fullName: response.displayName,
        firstName: response.firstName,
        candidateStatusId: response.candidateStatusId,
        startDate: new Date(response.startDate),
        careerHighlightNote: response.careerHighlightNote,
        canViewDesiredSalary: response.canViewDesiredSalary,
        desiredSalaryDescription: response.desiredSalaryDescription,
        desiredSalaryIsRate: response.desiredSalaryIsRate,
        links: response.links,
        attachments: converters.convertFileResponseList(response.attachments),
        email: response.email,
        phoneNumber: response.phoneNumber,
        canContactLead: response.canContactLead,
        primaryLocationName: response.primaryLocationName,
    };
    return highlights;
};

const convertAvailabilityResponse = (
    response: CandidateAvailabilityResponse
): CandidateAvailability => {
    const availability: CandidateAvailability = {
        interviewAvailabilityNote: response.interviewAvailabilityNote,
        noticeAvailability: converters.convertTagTypeResponse(
            response.noticeAvailability
        ),
        contactPreferences: converters.convertTagTypeResponseList(
            response.contactPreferences
        ),
        timeZoneId: response.timeZoneId,
    };

    return availability;
};

const convertCandidateJobDetailsResponse = (
    response: CandidateJobDetailsResponse
): CandidateJobDetails => {
    if (!response) return null;

    const job = new CandidateJobDetails();
    job.jobId = response.jobId;
    job.jobName = response.jobName;
    job.teamName = response.teamName;
    job.teamBackground = response.teamBackground;
    job.jobType = JobType.fromId(response.jobTypeId);
    job.location = converters.convertLocationResponse(response.location);
    job.contractEndDate = convertDateResponse(response.contractEndDate);
    job.contractStartDate = convertDateResponse(response.contractStartDate);
    job.hoursPerWeek = response.hoursPerWeek;
    job.billRateInUSDollars = response.billRateInUSDollars;
    job.jobSalary = response.jobSalaryInUSDollars;
    job.jobSalaryType = JobSalaryType.fromId(response.jobSalaryTypeId);
    job.status = JobStatus.fromId(response.jobStatusTypeId);
    return job;
};

const convertAdditionalInfo = (
    response: CandidateAdditionalInfoResponse
): CandidateAdditionalInfo => {
    if (!response) return null;
    const additionalInfo: CandidateAdditionalInfo = {
        interviewingElsewhereNote: response.interviewingElsewhereNote,
        isInterviewingElsewhere: response.isInterviewingElsewhere,
        isWillingToRelocate: convertLeadTagResponse(
            response.isWillingToRelocate
        ),
        isUserPrimaryLocationWithinJobLocation:
            response.isUserPrimaryLocationWithinJobLocation,
    };

    return additionalInfo;
};

const convertLeadTagResponse = (response: TagResponse): Tag => {
    if (!response) return null;

    const ret = converters.convertTagResponse(response);

    return ret;
};

const convertCandidateFeedbackResponse = (response: FeedbackResponse[]) =>
    response?.map(item => {
        const feedback: Feedback = {
            canEdit: item.canEdit,
            comment: item.comment,
            vote: item.vote,
            clientFullName: item.clientFullName,
            jobUserTypeId: item.jobUserTypeId,
            updateTimestamp: item.updateTimestamp,
        };

        return feedback;
    });

const convertCandidateNoFeedbackResponse = (response: ClientUserResponse[]) =>
    response?.map(item => {
        const noFeedback: NoFeedback = {
            clientFullName: item.clientFullName,
            jobUserTypeId: item.jobUserTypeId,
        };

        return noFeedback;
    });

const convertClientCandidateFeedbackResponse = (
    response: FeedbackDetailsResponse
) => {
    const feedbackResponse = response.feedback?.find(
        item => item.canEdit === true
    );
    const upVotes = response.feedback?.filter(item => item.vote === true);
    const downVotes = response.feedback?.filter(item => item.vote === false);

    const feedbackDetails: FeedbackDetails = {
        canViewFeedback: response.canViewFeedback,
        canGiveFeedback: response.canGiveFeedback,
        isOnJob: response.isOnJob,
        upVotesCount: upVotes ? upVotes.length : 0,
        downVotesCount: downVotes ? downVotes.length : 0,

        feedback: convertCandidateFeedbackResponse(response.feedback),
        clientsWithoutFeedback: convertCandidateNoFeedbackResponse(
            response.clientsWithoutFeedback
        ),
        clientFeedback: feedbackResponse,
    };

    return feedbackDetails;
};

const convertHiredDetailsResponse = (
    response: HiredDetailsResponse,
    jobResponse: CandidateJobDetailsResponse
) => {
    if (!response) return null;
    const jobType = JobType.fromId(jobResponse.jobTypeId);
    const hiredDetails: CandidateHiredDetails = new CandidateHiredDetails();

    hiredDetails.salaryTypeId = response.salaryTypeId;
    hiredDetails.salaryInUSDollars = response.salaryInUSDollars;
    hiredDetails.salaryDescription = response.salaryDescription;
    hiredDetails.salaryIsRate = response.salaryIsRate;
    hiredDetails.startDate = convertDateResponse(response.startDate);
    hiredDetails.endDate = jobType.isContractOrLocum()
        ? convertDateResponse(response.endDate)
        : null;
    hiredDetails.hoursPerWeek = response.hoursPerWeek;
    hiredDetails.duration = response.duration;
    hiredDetails.terminationDate = convertDateResponse(
        response.terminationDate
    );
    hiredDetails.canViewSalary = response.canViewSalary;
    hiredDetails.successFeeDescription = response.successFeeDescription;

    return hiredDetails;
};

const convertLeadActionsResponse = (response: LeadActionsResponse) => {
    if (!response) return null;

    const actions: CandidateActions = new CandidateActions();
    actions.positiveCandidateStatusId = response.positiveCandidateStatusId;
    actions.positiveCandidateStatusActionName =
        response.positiveCandidateStatusActionName;
    actions.negativeCandidateStatusId = response.negativeCandidateStatusId;
    actions.negativeCandidateStatusActionName =
        response.negativeCandidateStatusActionName;

    return actions;
};

const convertCandidateInterviewDetailsResponse = (
    res: CandidateInterviewDetailsResponse
) => {
    if (!res) return null;
    const interview: CandidateInterview = {
        candidateInterviewId: res.candidateInterviewId,
        interviewStatusTypeId: res.interviewStatusTypeId,
        interviewTypeId: res.interviewTypeId,
        startDateTime: res.startDateTime,
        interviewDate: res.interviewDate,
        interviewTime: res.interviewTime,
        durationHours: res.durationHours,
        durationMinutes: res.durationMinutes,
        timeZoneId: res.timeZoneId,
        interviewDetails: res.interviewDetails,
        sendNotification: res.sendNotification,
    };
    return interview;
};

const convertCandidateInterviewDetailsResponseList = (
    res: CandidateInterviewDetailsResponse[] = []
) => res.filter(i => !!i).map(i => convertCandidateInterviewDetailsResponse(i));

export const convertLeadJobDetailResponse = (
    response: LeadJobDetailResponse
): CandidateDetails => {
    const candidateDetails = new CandidateDetails();
    candidateDetails.status = JobCandidateStatus.fromId(
        response.highlights.candidateStatusId
    );
    candidateDetails.highlights = convertCandidateHighlights(
        response.highlights
    );
    candidateDetails.additionalInfo = convertAdditionalInfo(
        response.additionalInfo
    );
    candidateDetails.formattedResume = convertFileResponse(
        response.formattedResume
    );
    candidateDetails.availability = convertAvailabilityResponse(
        response.availability
    );
    candidateDetails.job = convertCandidateJobDetailsResponse(response.job);
    candidateDetails.jobManager =
        converters.convertJobManagerContactResponseNonStakeholder(
            response.jobManager
        );
    candidateDetails.candidateConcurrencyToken =
        response.candidateConcurrencyToken;

    candidateDetails.canViewCandidateActions = response.canViewCandidateActions;

    candidateDetails.feedbackDetails = convertClientCandidateFeedbackResponse(
        response.feedbackDetails
    );

    candidateDetails.hiredDetails = convertHiredDetailsResponse(
        response.hiredDetails,
        response.job
    );
    candidateDetails.actions = convertLeadActionsResponse(response.actions);
    candidateDetails.pastInterviews =
        convertCandidateInterviewDetailsResponseList(response.pastInterviews);
    candidateDetails.upcomingInterviews =
        convertCandidateInterviewDetailsResponseList(
            response.upcomingInterviews
        );

    candidateDetails.canOffer = response.canOffer;
    candidateDetails.canReject = response.canReject;
    candidateDetails.canEditHire = response.canEditHire;
    candidateDetails.canAddInterview = response.canAddInterview;
    candidateDetails.isCandidateRejectPromptRequired =
        response.isCandidateRejectPromptRequired;
    candidateDetails.hasMobileActions =
        ((!!response.actions?.negativeCandidateStatusId ||
            !!response.actions?.positiveCandidateStatusId) &&
            response.canViewCandidateActions) ||
        response.feedbackDetails?.canGiveFeedback;

    candidateDetails.pastInterviews =
        convertCandidateInterviewDetailsResponseList(response.pastInterviews);
    candidateDetails.upcomingInterviews =
        convertCandidateInterviewDetailsResponseList(
            response.upcomingInterviews
        );
    candidateDetails.qualifications = convertLeadJobQualificationsResponse(
        response.leadJobQualifications
    );

    return candidateDetails;
};
